import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalDialog,
    Sheet,
    Stack,
    Tab,
    TabList,
    TabPanel,
    Table,
    Tabs,
    Typography,
} from '@mui/joy'
import SearchIcon from '@mui/icons-material/Search'
import MergeIcon from '@mui/icons-material/MergeType'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import EastIcon from '@mui/icons-material/East'
import { migrationApi, MigrationPreview, MigrationResult } from './api'
import { billingApi } from '../billing/api'
import { MigrateOrgSubscriptionResult } from '../billing/types'
import { OrgSubscriptionMigrationStatus } from '@numeo/types-core'
import { Phase2MigrationPage } from './Phase2MigrationPage'

const OrgPreviewCard = ({ title, color, preview }: { title: string; color: 'primary' | 'warning'; preview: MigrationPreview }) => (
    <Card variant="outlined" sx={{ flex: 1 }}>
        <CardContent sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip variant="solid" color={color} size="sm">
                    {title}
                </Chip>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    {preview.applications.length} app{preview.applications.length !== 1 ? 's' : ''}
                </Typography>
            </Stack>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                <Table size="sm" stripe="odd" hoverRow>
                    <thead>
                        <tr>
                            <th style={{ width: '40%' }}>Company</th>
                            <th style={{ width: '20%' }}>MC#</th>
                            <th style={{ width: '40%' }}>Database</th>
                        </tr>
                    </thead>
                    <tbody>
                        {preview.applications.map((app) => (
                            <tr key={app.id}>
                                <td>
                                    <Typography level="body-sm" fontWeight="md">
                                        {app.company?.name || '—'}
                                    </Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{app.company?.mcNumber || '—'}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs" sx={{ fontFamily: 'monospace', fontSize: '11px' }}>
                                        {app.projectName}
                                    </Typography>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>
        </CardContent>
    </Card>
)

export const MigrationPage = () => {
    const [sourceEmail, setSourceEmail] = useState('')
    const [targetEmail, setTargetEmail] = useState('')
    const [sourcePreview, setSourcePreview] = useState<MigrationPreview | null>(null)
    const [targetPreview, setTargetPreview] = useState<MigrationPreview | null>(null)
    const [result, setResult] = useState<MigrationResult | null>(null)
    const [loadingSource, setLoadingSource] = useState(false)
    const [loadingTarget, setLoadingTarget] = useState(false)
    const [migrating, setMigrating] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [error, setError] = useState('')

    // Toggle Multi-MC
    const [toggleEmail, setToggleEmail] = useState('')
    const [toggling, setToggling] = useState(false)
    const [toggleResult, setToggleResult] = useState<{ multiMcEnabled: boolean; organizationId: string } | null>(null)
    const [toggleError, setToggleError] = useState('')

    // Org-level subscription migration
    const [subMigrateEmail, setSubMigrateEmail] = useState('')
    const [subMigrating, setSubMigrating] = useState(false)
    const [subMigrateResult, setSubMigrateResult] = useState<MigrateOrgSubscriptionResult | null>(null)
    const [subMigrateError, setSubMigrateError] = useState('')
    const [subConfirmOpen, setSubConfirmOpen] = useState(false)

    const handleLookup = async (side: 'source' | 'target') => {
        const email = side === 'source' ? sourceEmail : targetEmail
        if (!email.trim()) return
        const setLoading = side === 'source' ? setLoadingSource : setLoadingTarget
        const setPreview = side === 'source' ? setSourcePreview : setTargetPreview
        setLoading(true)
        setError('')
        setResult(null)
        try {
            const data = await migrationApi.preview(email.trim())
            setPreview(data)
        } catch (err: unknown) {
            setPreview(null)
            setError(`${side === 'source' ? 'Source' : 'Target'}: ${err instanceof Error ? err.message : 'Not found'}`)
        } finally {
            setLoading(false)
        }
    }

    const handleMigrate = async () => {
        if (!sourcePreview || !targetPreview) return
        setMigrating(true)
        setError('')
        try {
            const data = await migrationApi.migrate(targetEmail.trim(), sourceEmail.trim())
            setResult(data)
            setSourcePreview(null)
            setTargetPreview(null)
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Migration failed')
        } finally {
            setMigrating(false)
        }
    }

    const handleToggle = async (enabled: boolean) => {
        if (!toggleEmail.trim()) return
        setToggling(true)
        setToggleError('')
        setToggleResult(null)
        try {
            const data = await migrationApi.toggleMultiMc(toggleEmail.trim(), enabled)
            setToggleResult(data)
        } catch (err: unknown) {
            setToggleError(err instanceof Error ? err.message : 'Toggle failed')
        } finally {
            setToggling(false)
        }
    }

    const handleSubMigrate = async () => {
        if (!subMigrateEmail.trim()) return
        setSubMigrating(true)
        setSubMigrateError('')
        setSubMigrateResult(null)
        try {
            const preview = await migrationApi.preview(subMigrateEmail.trim())
            const appId = preview.applications[0]?.id
            if (!appId) throw new Error('No applications found for this owner')
            const data = await billingApi.migrateToOrgSubscription(appId)
            setSubMigrateResult(data)
        } catch (err: unknown) {
            setSubMigrateError(err instanceof Error ? err.message : 'Migration failed')
        } finally {
            setSubMigrating(false)
        }
    }

    const bothLoaded = sourcePreview && targetPreview
    const isSameOrg = sourcePreview?.sourceOrganization.id === targetPreview?.sourceOrganization.id

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Typography level="h3" sx={{ mb: 0.5 }}>
                Multi-MC Management
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 3 }}>
                Enable Multi-MC for organizations and migrate applications between owners.
            </Typography>

            <Tabs defaultValue="phase1" sx={{ background: 'transparent' }}>
                <TabList>
                    <Tab value="phase1">Phase 1 — Org merge</Tab>
                    <Tab value="phase2">Phase 2 — Per-MC split</Tab>
                    <Tab value="phase3">Phase 3 — Billing</Tab>
                </TabList>
                <TabPanel value="phase1" sx={{ px: 0 }}>
                    {/* Toggle Multi-MC */}
                    <Card variant="outlined" sx={{ mb: 4 }}>
                        <CardContent>
                            <Typography level="title-md" sx={{ mb: 2 }}>
                                Enable / Disable Multi-MC
                            </Typography>
                            <Stack direction="row" spacing={1.5} alignItems="flex-end">
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Owner Email</FormLabel>
                                    <Input
                                        value={toggleEmail}
                                        onChange={(e) => setToggleEmail(e.target.value)}
                                        placeholder="owner@example.com"
                                        name="toggle-owner-email"
                                        autoComplete="new-password"
                                    />
                                </FormControl>
                                <Button loading={toggling} onClick={() => handleToggle(true)} color="success" variant="solid" size="sm">
                                    Enable
                                </Button>
                                <Button loading={toggling} onClick={() => handleToggle(false)} color="neutral" variant="outlined" size="sm">
                                    Disable
                                </Button>
                            </Stack>
                            {toggleError && (
                                <Alert color="danger" variant="soft" sx={{ mt: 1.5 }} startDecorator={<WarningIcon />}>
                                    {toggleError}
                                </Alert>
                            )}
                            {toggleResult && (
                                <Alert
                                    color={toggleResult.multiMcEnabled ? 'success' : 'neutral'}
                                    variant="soft"
                                    sx={{ mt: 1.5 }}
                                    startDecorator={<CheckCircleIcon />}
                                >
                                    Multi-MC {toggleResult.multiMcEnabled ? 'enabled' : 'disabled'} for organization {toggleResult.organizationId}
                                </Alert>
                            )}
                        </CardContent>
                    </Card>

                    {/* Migration Section */}
                    <Typography level="h4" sx={{ mb: 0.5 }}>
                        Organization Transfer
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 3 }}>
                        Move all applications from one organization to another.
                    </Typography>

                    {/* Lookup both sides */}
                    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                        <Card variant="outlined" sx={{ flex: 1 }}>
                            <CardContent>
                                <Chip variant="solid" color="warning" size="sm" sx={{ mb: 1.5 }}>
                                    Source (from)
                                </Chip>
                                <FormControl>
                                    <FormLabel>Owner Email</FormLabel>
                                    <Stack direction="row" spacing={1}>
                                        <Input
                                            value={sourceEmail}
                                            onChange={(e) => setSourceEmail(e.target.value)}
                                            placeholder="source@example.com"
                                            name="source-owner-email"
                                            type="email"
                                            autoComplete="new-password"
                                            sx={{ flex: 1 }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleLookup('source')}
                                        />
                                        <Button loading={loadingSource} onClick={() => handleLookup('source')} variant="soft" size="sm">
                                            <SearchIcon />
                                        </Button>
                                    </Stack>
                                </FormControl>
                            </CardContent>
                        </Card>

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EastIcon sx={{ fontSize: 24, color: 'text.tertiary' }} />
                        </Box>

                        <Card variant="outlined" sx={{ flex: 1 }}>
                            <CardContent>
                                <Chip variant="solid" color="primary" size="sm" sx={{ mb: 1.5 }}>
                                    Target (to)
                                </Chip>
                                <FormControl>
                                    <FormLabel>Owner Email</FormLabel>
                                    <Stack direction="row" spacing={1}>
                                        <Input
                                            value={targetEmail}
                                            onChange={(e) => setTargetEmail(e.target.value)}
                                            placeholder="target@example.com"
                                            name="target-owner-email"
                                            type="email"
                                            autoComplete="new-password"
                                            sx={{ flex: 1 }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleLookup('target')}
                                        />
                                        <Button loading={loadingTarget} onClick={() => handleLookup('target')} variant="soft" size="sm">
                                            <SearchIcon />
                                        </Button>
                                    </Stack>
                                </FormControl>
                            </CardContent>
                        </Card>
                    </Stack>

                    {error && (
                        <Alert color="danger" variant="soft" sx={{ mb: 3 }} startDecorator={<WarningIcon />}>
                            {error}
                        </Alert>
                    )}

                    {/* Preview both sides */}
                    {(sourcePreview || targetPreview) && (
                        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                            {sourcePreview ? (
                                <OrgPreviewCard title="Source — will be emptied" color="warning" preview={sourcePreview} />
                            ) : (
                                <Card variant="outlined" sx={{ flex: 1, opacity: 0.5 }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                            Look up source owner
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <EastIcon sx={{ fontSize: 24, color: 'text.tertiary' }} />
                            </Box>

                            {targetPreview ? (
                                <OrgPreviewCard title="Target — will receive apps" color="primary" preview={targetPreview} />
                            ) : (
                                <Card variant="outlined" sx={{ flex: 1, opacity: 0.5 }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                            Look up target owner
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}
                        </Stack>
                    )}

                    {/* Merge button */}
                    {bothLoaded && (
                        <Card variant="outlined" sx={{ mb: 3, borderColor: isSameOrg ? 'danger.400' : 'warning.400' }}>
                            <CardContent>
                                {isSameOrg ? (
                                    <Alert color="danger" variant="soft">
                                        Source and target are the same organization. Cannot merge.
                                    </Alert>
                                ) : (
                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                        <Box>
                                            <Typography level="title-sm">
                                                Move {sourcePreview!.applications.length} app{sourcePreview!.applications.length !== 1 ? 's' : ''} from source
                                                to target
                                            </Typography>
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                Source org will be emptied. Target org will have all apps. This action is logged.
                                            </Typography>
                                        </Box>
                                        <Button onClick={() => setConfirmOpen(true)} color="warning" variant="solid" startDecorator={<MergeIcon />}>
                                            Merge
                                        </Button>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Success */}
                    {result && (
                        <Alert color="success" variant="soft" startDecorator={<CheckCircleIcon />}>
                            <div>
                                <Typography level="title-sm">Migration completed</Typography>
                                <Typography level="body-sm">
                                    Moved {result.movedApplicationIds.length} application{result.movedApplicationIds.length !== 1 ? 's' : ''} to target
                                    organization.
                                </Typography>
                            </div>
                        </Alert>
                    )}

                    {/* Confirmation Dialog */}
                    <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                        <ModalDialog variant="outlined" sx={{ maxWidth: 480, borderRadius: 'lg' }}>
                            <DialogTitle>
                                <WarningIcon sx={{ color: 'danger.500', mr: 1 }} />
                                Confirm Organization Merge
                            </DialogTitle>
                            <Divider />
                            <DialogContent>
                                <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                                    <Typography level="body-sm" fontWeight="lg">
                                        This action cannot be undone.
                                    </Typography>
                                </Alert>
                                <Typography level="body-sm" sx={{ mb: 1.5 }}>
                                    You are about to merge organizations:
                                </Typography>
                                <Sheet variant="soft" color="warning" sx={{ p: 1.5, borderRadius: 'sm', mb: 1 }}>
                                    <Typography level="body-xs" fontWeight="lg">
                                        Source (will be emptied)
                                    </Typography>
                                    <Typography level="body-sm">{sourceEmail}</Typography>
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                        {sourcePreview?.applications.length} application{sourcePreview?.applications.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Sheet>
                                <Sheet variant="soft" color="primary" sx={{ p: 1.5, borderRadius: 'sm', mb: 2 }}>
                                    <Typography level="body-xs" fontWeight="lg">
                                        Target (will receive apps)
                                    </Typography>
                                    <Typography level="body-sm">{targetEmail}</Typography>
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                        {targetPreview?.applications.length} existing app{targetPreview?.applications.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Sheet>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    All applications from source will be moved to target. The source organization will be emptied. This operation is logged with
                                    your admin identity.
                                </Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button variant="plain" color="neutral" onClick={() => setConfirmOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    loading={migrating}
                                    color="danger"
                                    variant="solid"
                                    onClick={async () => {
                                        setConfirmOpen(false)
                                        await handleMigrate()
                                    }}
                                >
                                    Proceed with Merge
                                </Button>
                            </DialogActions>
                        </ModalDialog>
                    </Modal>
                </TabPanel>
                <TabPanel value="phase2" sx={{ px: 0 }}>
                    <Phase2MigrationPage />
                </TabPanel>
                <TabPanel value="phase3" sx={{ px: 0 }}>
                    <Typography level="h4" sx={{ mb: 0.5 }}>
                        Org-Level Subscription
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 3 }}>
                        Migrate an org's subscription from app-level to org-level.
                    </Typography>
                    <Alert color="primary" variant="soft" sx={{ mb: 3 }}>
                        <div>
                            <Typography level="title-sm" sx={{ mb: 0.5 }}>
                                What happens after migration
                            </Typography>
                            <Typography level="body-sm" component="div">
                                <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                    <li>
                                        The best app-level subscription (active → trialing → past_due) is copied to <strong>org.subscription</strong>
                                    </li>
                                    <li>All apps in the org share one subscription — Stripe webhooks route to the org, not individual apps</li>
                                    <li>If no subscription exists on any app, a FREE placeholder is written so future purchases go org-level</li>
                                    <li>Safe to run multiple times — already-migrated orgs are skipped (no-op)</li>
                                </ul>
                            </Typography>
                        </div>
                    </Alert>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" spacing={1.5} alignItems="flex-end">
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Owner Email</FormLabel>
                                    <Input
                                        value={subMigrateEmail}
                                        onChange={(e) => setSubMigrateEmail(e.target.value)}
                                        placeholder="owner@example.com"
                                        onKeyDown={(e) => e.key === 'Enter' && handleSubMigrate()}
                                        autoComplete="new-password"
                                    />
                                </FormControl>
                                <Button
                                    loading={subMigrating}
                                    onClick={() => subMigrateEmail.trim() && setSubConfirmOpen(true)}
                                    color="primary"
                                    variant="solid"
                                    size="sm"
                                >
                                    Migrate
                                </Button>
                            </Stack>
                            {subMigrateError && (
                                <Alert color="danger" variant="soft" sx={{ mt: 1.5 }} startDecorator={<WarningIcon />}>
                                    {subMigrateError}
                                </Alert>
                            )}
                            {subMigrateResult && (
                                <Alert
                                    color={subMigrateResult.status === OrgSubscriptionMigrationStatus.ALREADY_MIGRATED ? 'neutral' : 'success'}
                                    variant="soft"
                                    sx={{ mt: 1.5 }}
                                    startDecorator={<CheckCircleIcon />}
                                >
                                    <div>
                                        <Typography level="title-sm">{subMigrateResult.status.replace(/_/g, ' ')}</Typography>
                                        <Typography level="body-sm">{subMigrateResult.message}</Typography>
                                        {subMigrateResult.plan && (
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                                Plan: {subMigrateResult.plan} · Org: {subMigrateResult.orgId}
                                            </Typography>
                                        )}
                                    </div>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>

                    <Modal open={subConfirmOpen} onClose={() => setSubConfirmOpen(false)}>
                        <ModalDialog variant="outlined" sx={{ maxWidth: 480, borderRadius: 'lg' }}>
                            <DialogTitle>
                                <WarningIcon sx={{ color: 'warning.500', mr: 1 }} />
                                Confirm Subscription Migration
                            </DialogTitle>
                            <Divider />
                            <DialogContent>
                                <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                                    <Typography level="body-sm" fontWeight="lg">
                                        Review the impact before proceeding.
                                    </Typography>
                                </Alert>
                                <Typography level="body-sm" sx={{ mb: 1.5 }}>
                                    You are about to migrate the subscription for:
                                </Typography>
                                <Sheet variant="soft" color="primary" sx={{ p: 1.5, borderRadius: 'sm', mb: 2 }}>
                                    <Typography level="body-xs" fontWeight="lg">
                                        Owner Email
                                    </Typography>
                                    <Typography level="body-sm">{subMigrateEmail}</Typography>
                                </Sheet>
                                <Typography level="body-sm" sx={{ mb: 1 }} fontWeight="lg">
                                    After migration:
                                </Typography>
                                <Typography level="body-sm" component="div" sx={{ mb: 2 }}>
                                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                        <li>
                                            The best app-level subscription is copied to <strong>org.subscription</strong>
                                        </li>
                                        <li>All apps in the org will share this single subscription</li>
                                        <li>Future Stripe webhooks route to the org, not individual apps</li>
                                        <li>Already-migrated orgs are skipped (no-op, safe to retry)</li>
                                    </ul>
                                </Typography>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    This operation is logged with your admin identity.
                                </Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button variant="plain" color="neutral" onClick={() => setSubConfirmOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    loading={subMigrating}
                                    color="primary"
                                    variant="solid"
                                    onClick={async () => {
                                        setSubConfirmOpen(false)
                                        await handleSubMigrate()
                                    }}
                                >
                                    Proceed with Migration
                                </Button>
                            </DialogActions>
                        </ModalDialog>
                    </Modal>
                </TabPanel>
            </Tabs>
        </Box>
    )
}
